// The org a page renders for: its display data, role and projects. `/$org` pages take the org from the URL;
// `/account` pages, which belong to the user, take the last used one. Kept apart from the shell so the route loaders
// that call it do not pull the shell into the first bundle.
import type { RouterContext } from "../router-context";
import { lastScope, projectIn } from "./scope";
import type { OrgInfo } from "./shell-state";

export type OrgData =
  | { missing: true; orgs: OrgInfo[] }
  | { missing: false; org: OrgInfo; orgs: OrgInfo[]; projects: string[]; userId: string };

type LoadContext = RouterContext & { session: { user: { id: string } } };

/** The user's orgs (cached), then the named org's projects and the caller's role in it; `client.org` follows. */
export async function loadOrg(context: LoadContext, name: string): Promise<OrgData> {
  const userId = context.session.user.id;
  const orgs = await context.queryClient.fetchQuery({ queryKey: ["user", "orgs", userId], queryFn: context.api.organizations });
  const infos: OrgInfo[] = orgs.map((o) => ({ name: o.metadata.name, displayName: o.spec?.displayName ?? o.metadata.name }));
  const org = infos.find((o) => o.name === name);
  if (!org) return { missing: true, orgs: infos };
  context.client.org = org.name;
  const [projects, whoami] = await Promise.all([
    context.queryClient.fetchQuery({ queryKey: [org.name, "", "projects"], queryFn: () => context.api.projects(org.name) }),
    context.queryClient.fetchQuery({ queryKey: [org.name, "", "whoami"], queryFn: () => context.client.get<{ role?: string }>("/auth/whoami", { org: org.name }) }),
  ]);
  // Signup and imported entitlements remain pending until email and card verification succeed.
  await context.client.post("/auth/starter-credit/claim", {}, { org: org.name });
  const withRole = { ...org, ...(whoami.role ? { role: whoami.role } : {}) };
  return {
    missing: false,
    org: withRole,
    orgs: infos.map((o) => (o.name === org.name ? withRole : o)),
    projects: projects.map((p) => p.metadata.name),
    userId,
  };
}

/** The project the shell shows: the wanted one if the org has it, else the last used, else its default. */
export function shellProject(data: Extract<OrgData, { missing: false }>, wanted?: string): string {
  return [wanted, lastScope(data.userId).project(data.org.name)].find((p) => p && data.projects.includes(p)) ?? projectIn(data.projects);
}
